import type {
  AnyDataGridContract,
  DataGridFacet,
  DataGridResponse,
  DataGridSchema,
  DataGridParameterSchema,
} from './types'

export function decodeDataGridResponse<Contract extends AnyDataGridContract>(
  contract: Contract,
) {
  return (raw: unknown): DataGridResponse<Contract> => {
    const response = record(raw, 'data grid response')
    if (!Array.isArray(response.items)) throw new Error('Malformed data grid items')
    const pageInfo = record(response.pageInfo, 'data grid page info')
    return {
      items: response.items.map((rawItem) => {
        const item = record(rawItem, 'data grid item')
        const values = record(item.values, 'data grid values')
        for (const column of Object.keys(values)) {
          const schema = contract.columns[column] ?? contract.supportingValues?.[column]
          if (!schema) {
            throw new Error(`Unexpected data grid column "${column}"`)
          }
          const value = values[column]
          if (value === null && !schema.nullable) {
            throw new Error(`Unexpected null for data grid column "${column}"`)
          }
          if (value !== null && schema.kind === 'number'
            && (typeof value !== 'number' || !Number.isInteger(value)
              || value < -2147483648 || value > 2147483647)) {
            throw new Error(`Malformed data grid value for "${column}"`)
          }
          if (value !== null && schema.kind !== 'value' && schema.kind !== 'number'
            && typeof value !== 'string') {
            throw new Error(`Malformed data grid value for "${column}"`)
          }
        }
        return {
          key: string(item.key, 'data grid item key'),
          values: values as DataGridResponse<Contract>['items'][number]['values'],
        }
      }),
      pageInfo: {
        hasNextPage: boolean(pageInfo.hasNextPage, 'hasNextPage'),
        endCursor: optionalString(pageInfo.endCursor, 'endCursor'),
      },
      schema: response.schema == null
        ? response.schema as null | undefined
        : decodeSchema<Contract>(response.schema),
      facets: response.facets == null
        ? response.facets as null | undefined
        : decodeFacets(response.facets) as DataGridResponse<Contract>['facets'],
    }
  }
}

function decodeSchema<Contract extends AnyDataGridContract>(
  raw: unknown,
): DataGridSchema<Contract> {
  const schema = record(raw, 'data grid schema')
  if (!Array.isArray(schema.columns) || !Array.isArray(schema.defaultSort)) {
    throw new Error('Malformed data grid schema')
  }
  return {
    id: string(schema.id, 'schema id') as Contract['id'],
    keyColumn: string(schema.keyColumn, 'schema key column') as Contract['keyColumn'],
    columns: schema.columns as DataGridSchema<Contract>['columns'],
    defaultSort: schema.defaultSort as DataGridSchema<Contract>['defaultSort'],
    searchMinimumLength: number(schema.searchMinimumLength, 'searchMinimumLength'),
    searchMode: schema.searchMode === 'contains' || schema.searchMode === 'fuzzy'
      ? schema.searchMode
      : undefined,
    parameters: Array.isArray(schema.parameters) ? schema.parameters.flatMap(decodeParameter) : undefined,
  }
}

function decodeParameter(raw: unknown): DataGridParameterSchema[] {
  const parameter = record(raw, 'data grid parameter')
  const id = string(parameter.id, 'parameter id')
  const label = string(parameter.label, 'parameter label')
  switch (parameter.type) {
    case 'boolean':
      if (typeof parameter.default !== 'boolean') throw new Error('Malformed data grid parameter')
      return [{ type: 'boolean', id, label, default: parameter.default }]
    default:
      return []
  }
}

function decodeFacets(raw: unknown): Record<string, DataGridFacet> {
  const facets = record(raw, 'data grid facets')
  return Object.fromEntries(Object.entries(facets).map(([key, value]) => {
    const facet = record(value, `facet ${key}`)
    return [key, {
      column: string(facet.column, 'facet column'),
      values: facet.values == null ? facet.values as null | undefined :
        array(facet.values, 'facet values').map((rawOption) => {
          const option = record(rawOption, 'facet value')
          return {
            value: string(option.value, 'facet value'),
            label: string(option.label, 'facet label'),
            count: number(option.count, 'facet count'),
          }
        }),
      hasMore: boolean(facet.hasMore, 'facet hasMore'),
      endCursor: optionalString(facet.endCursor, 'facet endCursor'),
      emptyCount: facet.emptyCount == null
        ? undefined
        : number(facet.emptyCount, 'facet emptyCount'),
      minimum: optionalString(facet.minimum, 'facet minimum'),
      maximum: optionalString(facet.maximum, 'facet maximum'),
    }]
  }))
}

function record(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Malformed ${field}`)
  }
  return value as Record<string, unknown>
}

function array(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`Malformed ${field}`)
  return value
}

function string(value: unknown, field: string): string {
  if (typeof value !== 'string') throw new Error(`Malformed ${field}`)
  return value
}

function optionalString(value: unknown, field: string): string | null | undefined {
  if (value === null || value === undefined) return value
  return string(value, field)
}

function boolean(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`Malformed ${field}`)
  return value
}

function number(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`Malformed ${field}`)
  return value
}
